import React, { useEffect, useState } from "react";
import { MdClose, MdConfirmationNumber, MdSend } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import TicketCategoryPicker from "../../../../components/ticketCategory/TicketCategoryPicker";
import AnexosInput from "../../../../components/anexos/AnexosInput";
import { mensagemDeErro } from "../../../../components/anexos/anexosRegras";
import { listClientTicketCategories, openClientTicket, uploadClientTicketAttachments } from "./ticketApi";
import {
  ClientTicket,
  ClientTicketCategory,
  ClientTicketImpact,
  ClientTicketUrgency,
  IMPACT_OPTIONS,
  URGENCY_OPTIONS,
} from "./ticketTypes";

interface Props {
  onClose: () => void;
  onCreated: (ticket: ClientTicket) => void;
}

/**
 * Formulário de abertura de chamado — D7: não existe campo de prioridade aqui. O cliente responde
 * sobre o mundo dele (quem foi afetado, o quanto atrapalha) e o servidor deriva a prioridade da
 * combinação das duas respostas.
 */
const OpenTicketModal: React.FC<Props> = ({ onClose, onCreated }) => {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [impact, setImpact] = useState<ClientTicketImpact | null>(null);
  const [urgency, setUrgency] = useState<ClientTicketUrgency | null>(null);
  const [categories, setCategories] = useState<ClientTicketCategory[]>([]);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [arquivos, setArquivos] = useState<File[]>([]);
  // Chamado já aberto cujos arquivos não subiram: "tentar de novo" reenvia só os arquivos.
  const [aberto, setAberto] = useState<ClientTicket | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Falha em silêncio de propósito: sem catálogo o formulário continua inteiro e o campo some.
  // Um toast de erro aqui assustaria quem só quer pedir ajuda, por causa de um campo OPCIONAL.
  useEffect(() => {
    listClientTicketCategories()
      .then(setCategories)
      .catch((e) => console.error("Erro ao carregar categorias", e));
  }, []);

  // `categoryId` NÃO entra: a categoria é opcional, e exigi-la trocaria uma abertura de chamado
  // por um formulário — justo com quem já está com um problema.
  // Com o chamado já aberto só os arquivos mudam: o reenvio não pode "reabrir" o formulário.
  const bloqueado = aberto !== null;
  const canSubmit =
    subject.trim().length > 0 && description.trim().length > 0 && impact !== null && urgency !== null;

  const handleSubmit = async () => {
    if (!canSubmit || impact === null || urgency === null) return;
    setSubmitting(true);
    let ticket = aberto;
    try {
      if (ticket === null) {
        ticket = await openClientTicket({
          subject: subject.trim(),
          description: description.trim(),
          impact,
          urgency,
          categoryId,
        });
      }
    } catch (e: any) {
      // Nunca genérico: a mensagem real do servidor é o que ajuda o cliente a entender o que falhou.
      toast.error(mensagemDeErro(e, "Erro ao abrir o chamado."));
      setSubmitting(false);
      return;
    }

    try {
      if (arquivos.length > 0) await uploadClientTicketAttachments(ticket.id, arquivos);
      toast.success(`Chamado ${ticket.number} aberto com sucesso.`);
      onCreated(ticket);
      onClose();
    } catch (e: any) {
      setAberto(ticket);
      onCreated(ticket); // o chamado existe: a lista precisa mostrá-lo
      const motivo = mensagemDeErro(e, "");
      toast.error(
        `Chamado ${ticket.number} aberto, mas os arquivos não subiram.${motivo ? ` ${motivo}` : ""} Clique em "Tentar enviar de novo".`
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-dv-lg dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative overflow-hidden bg-dv-gradient px-6 py-5 text-white">
          <div className="absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                <MdConfirmationNumber className="h-6 w-6" aria-hidden="true" />
              </div>
              <div>
                <p className="font-display text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">
                  Novo chamado
                </p>
                <h3 className="text-lg font-extrabold leading-tight">Conte o que está acontecendo</h3>
              </div>
            </div>
            <button
              aria-label="Fechar"
              onClick={onClose}
              className="rounded-xl p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            >
              <MdClose className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-5 overflow-auto px-6 py-5">
          <div>
            <label htmlFor="ticket-subject" className="mb-1 block text-sm font-semibold text-navy-700 dark:text-white">
              Assunto
            </label>
            <input
              id="ticket-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              readOnly={bloqueado}
              placeholder="Resuma em poucas palavras"
              maxLength={200}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </div>

          <div>
            <label htmlFor="ticket-description" className="mb-1 block text-sm font-semibold text-navy-700 dark:text-white">
              Descreva o que está acontecendo
            </label>
            <AnexosInput value={arquivos} onChange={setArquivos} disabled={submitting}>
              <textarea
                id="ticket-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                readOnly={bloqueado}
                placeholder="Quanto mais detalhe, mais rápido conseguimos ajudar"
                rows={4}
                className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              />
            </AnexosInput>
          </div>

          {categories.length > 0 && (
            <div>
              <label className="mb-1 block text-sm font-semibold text-navy-700 dark:text-white">
                Do que se trata? <span className="font-normal text-gray-500 dark:text-gray-400">(opcional)</span>
              </label>
              <TicketCategoryPicker
                categories={categories}
                value={categoryId}
                onChange={setCategoryId}
                disabled={bloqueado}
                hint="Ajuda a mandar o chamado direto para o time certo. Se não souber, pode deixar em branco."
              />
            </div>
          )}

          <ChoiceGroup
            legend="Quem isso afeta?"
            options={IMPACT_OPTIONS}
            value={impact}
            onChange={setImpact}
            name="ticket-impact"
            disabled={bloqueado}
          />

          <ChoiceGroup
            legend="O quanto isso está atrapalhando agora?"
            options={URGENCY_OPTIONS}
            value={urgency}
            onChange={setUrgency}
            name="ticket-urgency"
            disabled={bloqueado}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4 dark:border-gray-700">
          <button
            onClick={onClose}
            className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting || (aberto !== null && arquivos.length === 0)}
            className="dv-sheen flex items-center gap-2 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <MdSend className="h-4 w-4" aria-hidden="true" />
            {submitting ? "Enviando…" : aberto ? "Tentar enviar de novo" : "Abrir chamado"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Grupo de opções em cards clicáveis — mais fácil de escanear que um <select> para uma escolha só.
function ChoiceGroup<T extends number>({
  legend,
  options,
  value,
  onChange,
  name,
  disabled,
}: {
  legend: string;
  options: { value: T; label: string; hint: string }[];
  value: T | null;
  onChange: (v: T) => void;
  name: string;
  disabled?: boolean;
}) {
  return (
    <fieldset>
      <legend className="mb-2 block text-sm font-semibold text-navy-700 dark:text-white">{legend}</legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <label
              key={opt.value}
              className={`cursor-pointer rounded-xl border p-3 text-left transition-all ${
                active
                  ? "border-dv-blue-500 bg-dv-blue-50 ring-2 ring-dv-blue-500/20 dark:border-dv-cyan-400 dark:bg-dv-blue-900/20"
                  : "border-gray-200 bg-white hover:border-gray-300 dark:border-white/10 dark:bg-navy-900 dark:hover:border-white/20"
              }`}
            >
              <input
                type="radio"
                name={name}
                className="sr-only"
                checked={active}
                disabled={disabled}
                onChange={() => onChange(opt.value)}
              />
              <p className={`text-sm font-semibold ${active ? "text-dv-blue-700 dark:text-dv-cyan-300" : "text-navy-700 dark:text-white"}`}>
                {opt.label}
              </p>
              <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">{opt.hint}</p>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export default OpenTicketModal;
